<script lang="ts">
import type { Component } from 'vue'
import specialFrameSrc from '../assets/top-profile.svg'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'xl'

export interface AvatarProps {
  as?: string | Component
  src?: string
  alt?: string
  icon?: string
  iconClass?: string
  text: string
  size?: AvatarSize
  class?: string | object
  style?: string | object
  isRounded?: boolean
  overflowCount?: number
  isSpecial?: boolean
  badge?: string
}

export interface AvatarSlots {
  default: (props?: object) => any
}

export const AVATAR_BACKGROUND_CLASSES = [
  'bg-critical-600',
  'bg-critical-700',
  'bg-warning-600',
  'bg-warning-700',
  'bg-caution-600',
  'bg-caution-700',
  'bg-success-600',
  'bg-success-700',
]

export function getAvatarBackgroundClass(text: string) {
  let hash = 0
  for (let i = 0; i < text.length; i++)
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_BACKGROUND_CLASSES[Math.abs(hash) % AVATAR_BACKGROUND_CLASSES.length]
}

function getAvatarInitials(text: string) {
  const trimmedText = text.trim()
  if (trimmedText.includes('@'))
    return trimmedText.split('@')[0].replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2).toUpperCase()

  const words = trimmedText.split(/\s+/).filter(Boolean)
  return words.slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
}

const ui = {
  root: 'inline-flex items-center justify-center shrink-0 select-none align-middle font-semibold',
  border: 'border border-secondary-100',
  squareRadius: 'rounded-md',
  image: 'h-full w-full rounded-[inherit] object-cover',
  label: 'text-white leading-none text-muted truncate uppercase',
  icon: 'text-purple text-muted shrink-0',
  silhouette: 'bg-secondary-400',
  silhouetteIcon: 'text-white shrink-0',
  decorated: 'relative',
  special: 'isolate m-0.75 !bg-transparent',
  specialLayer: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-43px max-w-none',
  specialFill: '-z-1',
  specialFrame: 'pointer-events-none z-1',
  specialImage: 'object-cover',
  specialClipPath: 'polygon(-0.4% 47.8%, 1.1% 45.5%, 17.9% 18.7%, 18.7% 17.9%, 45.5% 1.1%, 47.8% -0.4%, 50.7% -0.4%, 52.9% 1.1%, 79.7% 17.9%, 80.5% 18.7%, 97.3% 45.5%, 98.8% 47.8%, 98.8% 50.7%, 97.3% 52.9%, 80.5% 79.7%, 79.7% 80.5%, 52.9% 97.3%, 50.7% 98.8%, 47.8% 98.8%, 45.5% 97.3%, 18.7% 80.5%, 17.9% 79.7%, 1.1% 52.9%, -0.4% 50.7%)',
  badge: 'pointer-events-none absolute left-1/2 -translate-x-1/2 z-2 font-bold text-white whitespace-nowrap',
  badgeStyle: { fontSize: '5.941px', lineHeight: '7.129px', padding: '2.376px 3.565px', borderRadius: '2.376px', textShadow: '0 1px 4px rgba(0, 0, 0, 0.25)' },
  newBadge: '-bottom-px bg-critical-500',
  specialBadge: '-bottom-1',
  specialBadgeBackground: 'linear-gradient(270deg, #e49f00 0%, #ffe5a6 20%, #f3c866 38%, #db9a06 100%)',
  sizes: {
    xs: {
      root: 'text-xs size-22px',
      icon: 'size-3.5',
    },
    sm: {
      root: 'text-xs size-25px',
      icon: 'size-4',
    },
    md: {
      root: 'text-sm size-28px',
      icon: 'size-4.5',
    },
    xl: {
      root: 'text-base size-36px',
      icon: 'size-6',
    },
  },
}
</script>

<script setup lang="ts">
import { Primitive, Slot } from 'reka-ui'
import { computed, ref, useSlots, watch } from 'vue'
import AwIcon from './AwIcon.vue'

defineOptions({ inheritAttrs: false })

const {
  as = 'span',
  src,
  alt,
  icon,
  text,
  size = 'md',
  class: propsClass,
  style,
  isRounded = true,
  overflowCount,
  isSpecial = false,
  badge,
} = defineProps<AvatarProps>()

const label = computed(() => overflowCount === undefined ? getAvatarInitials(text) : `+${overflowCount}`)
const hasError = ref(false)
const slots = useSlots()
const hasImage = computed(() => !!src && !hasError.value)
const isSilhouette = computed(() => !hasImage.value && !slots.default && !icon && !label.value)
const hasCustomBackground = computed(() => !isSpecial && typeof propsClass === 'string' && propsClass.includes('bg-'))
const backgroundClass = computed(() => {
  if (hasCustomBackground.value)
    return undefined
  return isSilhouette.value ? ui.silhouette : getAvatarBackgroundClass(text)
})
const sizeClass = computed(() => ui.sizes[size].root)
const rootClass = computed(() => [
  ui.root,
  !isSpecial && ui.border,
  !isSpecial && (isRounded ? 'rounded-full' : ui.squareRadius),
  sizeClass.value,
  !isSpecial && backgroundClass.value,
  (isSpecial || badge) && ui.decorated,
  isSpecial && ui.special,
  propsClass,
])
const specialClipStyle = { clipPath: ui.specialClipPath }
const sizePx = computed(() => ({ xs: 22, sm: 25, md: 28, xl: 36 })[size])

watch(() => src, () => {
  if (hasError.value)
    hasError.value = false
})

function onError() {
  hasError.value = true
}
</script>

<template>
  <Primitive
    :as
    :style
    :class="rootClass"
  >
    <span v-if="isSpecial" :class="[ui.specialLayer, ui.specialFill, backgroundClass]" :style="specialClipStyle" />
    <img
      v-if="src && !hasError"
      role="img"
      :src :alt :width="sizePx" :height="sizePx"
      v-bind="$attrs"
      :class="isSpecial ? [ui.specialLayer, ui.specialImage] : ui.image"
      :style="isSpecial ? specialClipStyle : undefined"
      @error="onError"
    >
    <Slot v-else v-bind="$attrs">
      <slot>
        <AwIcon v-if="icon" :name="icon" :class="[ui.icon, ui.sizes[size].icon, iconClass ?? '']" />
        <span v-else-if="label" :class="ui.label">{{ label }}</span>
        <AwIcon v-else name="i-ph-user-fill" :class="[ui.silhouetteIcon, ui.sizes[size].icon]" />
      </slot>
    </Slot>
    <img v-if="isSpecial" :src="specialFrameSrc" alt="" :class="[ui.specialLayer, ui.specialFrame]">
    <span
      v-if="badge"
      :class="[ui.badge, isSpecial ? ui.specialBadge : ui.newBadge]"
      :style="[ui.badgeStyle, isSpecial ? { background: ui.specialBadgeBackground } : undefined]"
    >{{ badge }}</span>
  </Primitive>
</template>
